// Shoelace wird ueber das vorgebundelte cdn/-Bundle aus index.html geladen.
// Der Autoloader registriert die Komponenten on demand sobald ein <sl-*>-Tag im DOM auftaucht.
// Hier nur den Asset-Pfad konfigurieren - alle Shoelace-Imports laufen ueber die Autoloader-URL.
declare global {
    interface Window { __SHOELACE_AUTOLOADER_READY?: Promise<void>; }
}

// Dark Mode init: aus localStorage, sonst nach Systempraeferenz.
export function applyTheme (): void {
    const stored = localStorage.getItem ("trackme.theme");
    const wantDark = stored === "dark"
        || (stored === null && window.matchMedia ("(prefers-color-scheme: dark)").matches);
    document.body.classList.toggle ("sl-theme-dark", wantDark);
    document.body.classList.toggle ("sl-theme-light", !wantDark);
}
applyTheme ();
window.matchMedia ("(prefers-color-scheme: dark)").addEventListener ("change", () => {
    if (localStorage.getItem ("trackme.theme") === null) applyTheme ();
});

// Service-Worker-Registrierung. Wurde frueher inline in index.html gemacht,
// ist hier untergebracht damit die CSP `script-src` ohne `'unsafe-inline'` auskommt.
if ("serviceWorker" in navigator) {
    window.addEventListener ("load", () => {
        navigator.serviceWorker.register ("/sw.js").catch (() => { /* still */ });
    });
}

import { isLoggedIn, getDisplayName, clearSession, api } from "./api.js";
import { renderLogin } from "./views/login.js";
import { renderRecipes } from "./views/recipes.js";
import { renderConsumption } from "./views/consumption.js";
import { renderSettings } from "./views/settings.js";
import { renderStats } from "./views/stats.js";

type Route = "login" | "consumption" | "recipes" | "settings" | "stats";

function currentRoute (): Route {
    const hash = location.hash.replace (/^#\/?/, "");

    if (hash === "recipes") return "recipes";
    if (hash === "settings") return "settings";
    if (hash === "stats") return "stats";
    if (hash === "login") return "login";

    return "consumption";
}

function navTo (route: Route): void {
    location.hash = `#/${route}`;
}

function renderNav (root: HTMLElement, active: Route): void {
    const nav = document.createElement ("nav");
    nav.className = "app-nav";

    const links: Array<[Route, string]> = [
        ["consumption", "Dashboard"],
        ["recipes", "Rezepte"],
        ["stats", "Statistik"],
        ["settings", "Einstellungen"],
    ];

    for (const [r, label] of links) {
        const btn = document.createElement ("sl-button");
        btn.setAttribute ("size", "small");
        btn.setAttribute ("variant", r === active ? "primary" : "default");
        btn.textContent = label;
        btn.addEventListener ("click", () => navTo (r));
        nav.appendChild (btn);
    }

    const spacer = document.createElement ("span");
    spacer.style.flex = "1";
    nav.appendChild (spacer);

    const name = getDisplayName ();

    if (name !== null) {
        const who = document.createElement ("span");
        who.className = "muted";
        who.textContent = name;
        nav.appendChild (who);
    }

    const logout = document.createElement ("sl-button");
    logout.setAttribute ("size", "small");
    logout.setAttribute ("variant", "text");
    logout.textContent = "Logout";
    logout.addEventListener ("click", async () => {
        try { await api.logout (); } catch { /* ignore */ }
        clearSession ();
        navTo ("login");
    });
    nav.appendChild (logout);

    root.appendChild (nav);
}

async function render (): Promise<void> {
    const root = document.getElementById ("app") as HTMLElement;
    root.innerHTML = "";

    if (!isLoggedIn ()) {
        renderLogin (root, () => navTo ("consumption"));
        return;
    }

    const route = currentRoute ();
    renderNav (root, route);

    const view = document.createElement ("div");
    root.appendChild (view);

    if (route === "recipes") {
        await renderRecipes (view);
    } else if (route === "settings") {
        await renderSettings (view);
    } else if (route === "stats") {
        await renderStats (view);
    } else {
        await renderConsumption (view);
    }
}

window.addEventListener ("hashchange", () => { void render (); });
window.addEventListener ("DOMContentLoaded", () => { void render (); });
